import { useMemo } from "react";
import {
  PATIENT_SOURCE_VENDOR,
  type ConditionStatus,
  type ParsedRecord,
  type RecordDuplicates,
} from "@/features/patient/records/api/dataPipelineApi";
import type { EmpiRecordGolden } from "@/shared/types/empi";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";
import { formatDose } from "@/shared/lib/formatDose";
import { observationKind, type ObservationKind } from "@/features/patient/diagnostics/utils/adaptDiagnostics";

// ── shared field helpers ────────────────────────────────────────────────────
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const dateOnly = (v: unknown): string =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : "";
const firstStr = (...vals: unknown[]): string => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return "";
};
const firstDate = (...vals: unknown[]): string => {
  for (const v of vals) {
    const d = dateOnly(v);
    if (d) return d;
  }
  return "";
};

// ────────────────────────────────────────────────────────────────────────────
// Diagnoses  ←  Condition
// ────────────────────────────────────────────────────────────────────────────

// ── Active / inactive / resolved, and who said so ─────────────────────────────
//
// The hospital's status, unless the patient marked it themselves (decided
// 2026-10-21) — and the hospital's record wins again only when it's dated
// after the patient's mark ("newer information wins", worked out by the API).

export interface ConditionStatusInfo {
  /** The status shown. */
  status: ConditionStatus;
  /** What the hospital's record says. */
  hospitalStatus: ConditionStatus;
  /** The patient's own mark, when there is one. */
  mark: { status: ConditionStatus; setAt: string } | null;
  /** A newer hospital record overrides the patient's mark (shown with a note). */
  hospitalNewer: boolean;
  /** A provider's record is marked through the API; the patient's own concern is edited directly. */
  source: "provider" | "patient";
}

export function hospitalConditionStatus(clinicalStatus: string): ConditionStatus {
  const s = clinicalStatus.toLowerCase();
  if (s.includes("resolv")) return "resolved";
  if (s.includes("inactive") || s.includes("remission")) return "inactive";
  return "active"; // active, recurrence, relapse, or not stated
}

export function conditionStatusInfo(record: ParsedRecord): ConditionStatusInfo {
  const hospitalStatus = hospitalConditionStatus(str(record.fields.clinical_status));
  const own = record.patientStatus ?? null;
  const source = record.sourceVendor === PATIENT_SOURCE_VENDOR ? "patient" : "provider";
  return {
    status: own && !own.hospitalNewer ? own.status : hospitalStatus,
    hospitalStatus,
    mark: own ? { status: own.status, setAt: own.setAt } : null,
    hospitalNewer: own?.hospitalNewer ?? false,
    source,
  };
}

/** A real "last updated": the hospital's latest recorded date, or the day the
 *  patient marked it — never the day MedSync stored the record (that showed a
 *  2019 condition as "Last Updated" on its upload day). The patient's own
 *  concern: the last time they changed it. */
export function conditionLastUpdated(record: ParsedRecord): string {
  if (record.sourceVendor === PATIENT_SOURCE_VENDOR) return dateOnly(record.updatedAt);
  const f = record.fields;
  const days = [dateOnly(f.recorded_date), dateOnly(f.asserted_date), dateOnly(record.patientStatus?.setAt)].filter(Boolean);
  return days.sort().at(-1) ?? "";
}

export interface DiagnosisRecord {
  id: string;
  name: string;
  icdCode: string; // "" when unknown
  snomedCode: string; // "" when unknown
  diagnosedDate: string; // "" when unknown
  diagnosedBy: string; // who recorded it (records API clinician_name); "" when unknown
  location: string; // where (the linked visit's place); "" when unknown
  status: "active" | "chronic" | "resolved";
  severity: string; // "" — not part of the FHIR Condition data we store
  description: string; // "" when unknown (falls back to the record's free-text note)
  plainLanguageSummary: string; // "" — no AI summary is generated for conditions yet
  relatedRecords: string[];
  clinicalStatus: string;
  verificationStatus: string;
  /** Last recorded by the hospital or marked by the patient (YYYY-MM-DD); "" when unknown. */
  lastUpdated: string;
  /** Active / inactive / resolved as shown, and who said so. */
  statusInfo: ConditionStatusInfo;
  /** Found in several uploads/syncs and shown once (combined view). */
  duplicates?: RecordDuplicates | null;
  /** Module 5: a resolved EMPI cluster's golden record (Condition / AllergyIntolerance). */
  golden?: EmpiRecordGolden | null;
}

function mapConditionStatus(clinicalStatus: string): DiagnosisRecord["status"] {
  const s = clinicalStatus.toLowerCase();
  if (s.includes("resolv") || s.includes("inactive") || s.includes("remission")) {
    return "resolved";
  }
  return "active";
}

export function adaptDiagnosis(record: ParsedRecord): DiagnosisRecord {
  const f = record.fields;
  const clinicalStatus = str(f.clinical_status);
  const statusInfo = conditionStatusInfo(record);
  return {
    id: record.id,
    name: firstStr(f.display_name) || "Unspecified condition",
    icdCode: str(f.icd10_code),
    snomedCode: str(f.snomed_code),
    diagnosedDate: firstDate(f.onset_date, f.recorded_date, f.asserted_date),
    diagnosedBy: record.sourceVendor === PATIENT_SOURCE_VENDOR ? "you" : str(f.clinician_name),
    location: str(f.location_name),
    // Complete History's Current / Resolved list follows the status shown (the patient's mark included).
    status: statusInfo.status === "active" ? "active" : "resolved",
    severity: "",
    description: str(f.note),
    plainLanguageSummary: "",
    relatedRecords: typeof f.encounter_ref === "string" && f.encounter_ref ? [f.encounter_ref] : [],
    clinicalStatus,
    verificationStatus: str(f.verification_status),
    lastUpdated: conditionLastUpdated(record),
    statusInfo,
    duplicates: record.duplicates ?? null,
    golden: record.golden ?? null,
  };
}

export interface DiagnosesResult {
  diagnoses: DiagnosisRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDiagnoses(): DiagnosesResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  const diagnoses = useMemo(() => {
    return records
      .filter((r) => r.resourceType === "Condition")
      .map(adaptDiagnosis)
      .sort((a, b) => b.diagnosedDate.localeCompare(a.diagnosedDate));
  }, [records]);
  return { diagnoses, isLoading, error, refresh };
}

// ────────────────────────────────────────────────────────────────────────────
// Complete History  ←  Procedure + Condition + AllergyIntolerance / FamilyMemberHistory
// ────────────────────────────────────────────────────────────────────────────

export interface MedicalHistoryRecord {
  id: string;
  type: "surgery" | "hospitalization" | "procedure" | "condition" | "allergy";
  title: string;
  date: string; // "" when unknown
  provider: string; // "" when unknown
  facility: string; // "" when unknown
  notes: string; // "" when unknown
  outcome: string; // "" when unknown
  /** Found in several uploads/syncs and shown once (combined view). */
  duplicates?: RecordDuplicates | null;
  /** Module 5: a resolved EMPI cluster's golden record (Condition / AllergyIntolerance). */
  golden?: EmpiRecordGolden | null;
}

export interface FamilyHistoryRecord {
  id: string;
  relation: string;
  relationKey: string; // normalised label used to slot the genogram
  condition: string; // "" when unknown
  ageAtOnset?: number;
  status: "living" | "deceased";
  notes: string; // "" when unknown
  sex: string;
}

function mapProcedureType(display: string, category: string): MedicalHistoryRecord["type"] {
  const hay = `${display} ${category}`.toLowerCase();
  if (hay.includes("surg") || hay.includes("operat") || hay.includes("ectomy") || hay.includes("plasty")) {
    return "surgery";
  }
  if (hay.includes("admission") || hay.includes("inpatient") || hay.includes("hospital")) {
    return "hospitalization";
  }
  return "procedure";
}

export function adaptMedicalHistory(record: ParsedRecord): MedicalHistoryRecord {
  return { ...adaptMedicalHistoryFields(record), duplicates: record.duplicates ?? null, golden: record.golden ?? null };
}

function adaptMedicalHistoryFields(record: ParsedRecord): MedicalHistoryRecord {
  const f = record.fields;
  if (record.resourceType === "Procedure") {
    return {
      id: record.id,
      type: mapProcedureType(str(f.display_name), str(f.category)),
      title: firstStr(f.display_name) || "Procedure",
      date: firstDate(f.performed_date, f.performed_start, f.performed_end),
      provider: "",
      facility: "",
      notes: firstStr(f.reason_display),
      outcome: "",
    };
  }
  if (record.resourceType === "AllergyIntolerance") {
    return {
      id: record.id,
      type: "allergy",
      title: firstStr(f.display_name, f.code) || "Allergy",
      date: firstDate(f.onset_date, f.recorded_date),
      provider: "",
      facility: "",
      notes: firstStr(f.reaction_manifestation, f.reaction_description),
      outcome: firstStr(f.criticality),
    };
  }
  // Condition
  return {
    id: record.id,
    type: "condition",
    title: firstStr(f.display_name) || "Past condition",
    date: firstDate(f.onset_date, f.recorded_date, f.asserted_date),
    provider: "",
    facility: "",
    notes: str(f.note),
    outcome: firstStr(f.clinical_status),
  };
}

// ── Complete History, Medical tab: one shape per kind of record ─────────────

export type AllergySeverity = "high" | "moderate" | "low" | "unknown";

export interface AllergyRecord {
  id: string;
  name: string;
  /** "medication" | "food" | "environment" | ... ; "" when unknown */
  category: string;
  severity: AllergySeverity;
  reaction: string; // "" when unknown
  /** active / inactive / resolved, as the source wrote it; "" when unknown */
  status: string;
  notedDate: string; // "" when unknown
  recordedBy: string; // records API clinician_name; "" when unknown
  location: string; // records API location_name; "" when unknown
  duplicates?: RecordDuplicates | null;
  golden?: EmpiRecordGolden | null;
}

const SEVERITY_ORDER: Record<AllergySeverity, number> = { high: 0, moderate: 1, low: 2, unknown: 3 };

function mapAllergySeverity(criticality: string): AllergySeverity {
  const s = criticality.toLowerCase();
  if (s.includes("high") || s.includes("severe")) return "high";
  if (s.includes("moderate") || s.includes("medium")) return "moderate";
  if (s.includes("low") || s.includes("mild")) return "low";
  return "unknown";
}

export function adaptAllergy(record: ParsedRecord): AllergyRecord {
  const f = record.fields;
  return {
    id: record.id,
    name: firstStr(f.display_name, f.code) || "Allergy",
    category: str(f.category).toLowerCase(),
    severity: mapAllergySeverity(str(f.criticality)),
    reaction: firstStr(f.reaction_manifestation, f.reaction_description),
    status: str(f.clinical_status).toLowerCase(),
    notedDate: firstDate(f.onset_date, f.recorded_date),
    recordedBy: str(f.clinician_name),
    location: str(f.location_name),
    duplicates: record.duplicates ?? null,
    golden: record.golden ?? null,
  };
}

/** Most severe first, then by name. */
export function sortAllergies(allergies: AllergyRecord[]): AllergyRecord[] {
  return [...allergies].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || a.name.localeCompare(b.name),
  );
}

export interface ProcedureRecord {
  id: string;
  name: string;
  code: string; // CPT; "" when unknown
  date: string; // "" when unknown
  reason: string; // "" when unknown
  status: string; // "" when unknown
  performedBy: string; // records API clinician_name; "" when unknown
  location: string; // records API location_name; "" when unknown
  duplicates?: RecordDuplicates | null;
  golden?: EmpiRecordGolden | null;
}

export function adaptProcedure(record: ParsedRecord): ProcedureRecord {
  const f = record.fields;
  return {
    id: record.id,
    name: firstStr(f.display_name) || "Procedure",
    code: str(f.cpt_code),
    date: firstDate(f.performed_date, f.performed_start, f.performed_end),
    reason: firstStr(f.reason_display, f.note),
    status: str(f.status).toLowerCase(),
    performedBy: str(f.clinician_name),
    location: str(f.location_name),
    duplicates: record.duplicates ?? null,
    golden: record.golden ?? null,
  };
}

export interface ImmunizationRecord {
  id: string;
  name: string;
  cvxCode: string; // "" when unknown
  date: string; // "" when unknown
  dose: string; // "0.5 mL"; "" when unknown
  site: string; // "" when unknown
  route: string; // "" when unknown
  lotNumber: string; // "" when unknown
  /** "completed" normally; anything else (not-done, entered-in-error) is shown as such. */
  status: string;
  givenBy: string; // records API clinician_name; "" when unknown
  location: string; // records API location_name; "" when unknown
  note: string; // "" when unknown
  duplicates?: RecordDuplicates | null;
  golden?: EmpiRecordGolden | null;
}

export function adaptImmunization(record: ParsedRecord): ImmunizationRecord {
  const f = record.fields;
  const doseQuantity = f.dose_quantity != null ? String(f.dose_quantity) : "";
  return {
    id: record.id,
    name: firstStr(f.vaccine_display) || "Vaccine",
    cvxCode: str(f.cvx_code),
    date: firstDate(f.occurrence_date),
    dose: formatDose(doseQuantity, str(f.dose_unit)),
    site: str(f.site),
    route: str(f.route),
    lotNumber: str(f.lot_number),
    status: str(f.status).toLowerCase(),
    givenBy: str(f.clinician_name),
    location: str(f.location_name),
    note: str(f.note),
    duplicates: record.duplicates ?? null,
    golden: record.golden ?? null,
  };
}

const RELATION_SLOTS = [
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Paternal Grandfather",
  "Paternal Grandmother",
  "Maternal Grandfather",
  "Maternal Grandmother",
];

function normaliseRelation(raw: string): string {
  const s = raw.trim().toLowerCase();
  const match = RELATION_SLOTS.find((slot) => slot.toLowerCase() === s);
  if (match) return match;
  if (s === "dad") return "Father";
  if (s === "mom" || s === "mum") return "Mother";
  if (s === "grandfather" || s === "grandpa") return "Paternal Grandfather";
  if (s === "grandmother" || s === "grandma") return "Maternal Grandmother";
  // Title-case whatever the vendor sent so it still renders sensibly.
  return raw
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function adaptFamilyHistory(record: ParsedRecord): FamilyHistoryRecord {
  const f = record.fields;
  const relationRaw = firstStr(f.relationship_display, f.relationship_code) || "Family member";
  const ageRaw = str(f.condition_onset_age).match(/\d+/);
  return {
    id: record.id,
    relation: relationRaw,
    relationKey: normaliseRelation(relationRaw),
    condition: str(f.condition_display),
    ageAtOnset: ageRaw ? Number(ageRaw[0]) : undefined,
    status: f.deceased === true ? "deceased" : "living",
    notes: str(f.note),
    sex: str(f.sex),
  };
}

// ── Exam & Vitals ←  Observation (not a lab) ──────────────────────────────────
//
// Physical-exam findings, vital signs and other non-lab observations, by visit
// day (decided 2026-10-21: they used to be shown inside lab reports).

export interface ExamVitalItem {
  id: string;
  kind: Exclude<ObservationKind, "lab">;
  name: string;
  value: string;
  golden: EmpiRecordGolden | null;
}

export interface ExamVitalsVisit {
  date: string; // "" when not recorded
  vitals: ExamVitalItem[];
  exam: ExamVitalItem[];
  other: ExamVitalItem[];
}

/** "FINDINGS - PHYSICAL EXAM - SKIN EXAM - SKIN WARM" -> "Skin exam - Skin warm". */
export function tidyFindingName(raw: string): string {
  const parts = raw.split(/\s+-\s+/).map((p) => p.trim()).filter(Boolean)
    .filter((p) => !/^(findings?|physical exam(ination)?)$/i.test(p));
  const kept = parts.length ? parts : [raw.trim()];
  // ALL-CAPS source text reads as a sentence per part; anything else is kept as written.
  const shout = kept.join(" ") === kept.join(" ").toUpperCase();
  return kept.map((p) => (shout ? p.charAt(0) + p.slice(1).toLowerCase() : p)).join(" - ");
}

export function examAndVitalsVisits(records: ParsedRecord[]): ExamVitalsVisit[] {
  const visits = new Map<string, ExamVitalsVisit>();
  for (const r of records) {
    if (r.resourceType !== "Observation") continue;
    const kind = observationKind(r.fields);
    if (kind === "lab") continue;
    const f = r.fields;
    const date = firstDate(f.effective_date, f.issued);
    const value = [firstStr(f.value_quantity, f.value_string, f.value_codeable_text), str(f.value_unit)].filter(Boolean).join(" ");
    const name = firstStr(f.display_name) || "Observation";
    const item: ExamVitalItem = { id: r.id, kind, name: kind === "exam" ? tidyFindingName(name) : name, value, golden: r.golden ?? null };
    const visit = visits.get(date) ?? { date, vitals: [], exam: [], other: [] };
    (kind === "vital" ? visit.vitals : kind === "exam" ? visit.exam : visit.other).push(item);
    visits.set(date, visit);
  }
  return [...visits.values()].sort((a, b) => b.date.localeCompare(a.date));
}

export interface ClinicalHistoryResult {
  medicalHistory: MedicalHistoryRecord[];
  /** The Medical tab's three lists. */
  allergies: AllergyRecord[];
  conditions: DiagnosisRecord[];
  procedures: ProcedureRecord[];
  immunizations: ImmunizationRecord[];
  /** Exam findings, vitals and other non-lab observations, by visit day. */
  examAndVitals: ExamVitalsVisit[];
  familyHistory: FamilyHistoryRecord[];
  /** Always empty — social history is not extracted by the pipeline yet. */
  socialHistory: never[];
  hasSocialSource: false;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useClinicalHistory(): ClinicalHistoryResult {
  const { records, isLoading, error, refresh } = useParsedRecords();

  const medicalHistory = useMemo(() => {
    return records
      .filter(
        (r) =>
          r.resourceType === "Procedure" ||
          r.resourceType === "Condition" ||
          r.resourceType === "AllergyIntolerance",
      )
      .map(adaptMedicalHistory)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [records]);

  const familyHistory = useMemo(() => {
    return records
      .filter((r) => r.resourceType === "FamilyMemberHistory")
      .map(adaptFamilyHistory);
  }, [records]);

  const allergies = useMemo(
    () => sortAllergies(records.filter((r) => r.resourceType === "AllergyIntolerance").map(adaptAllergy)),
    [records],
  );
  const conditions = useMemo(
    () =>
      records
        .filter((r) => r.resourceType === "Condition")
        .map(adaptDiagnosis)
        .sort((a, b) => b.diagnosedDate.localeCompare(a.diagnosedDate)),
    [records],
  );
  const procedures = useMemo(
    () =>
      records
        .filter((r) => r.resourceType === "Procedure")
        .map(adaptProcedure)
        .sort((a, b) => b.date.localeCompare(a.date)),
    [records],
  );

  const immunizations = useMemo(
    () =>
      records
        .filter((r) => r.resourceType === "Immunization")
        .map(adaptImmunization)
        .sort((a, b) => b.date.localeCompare(a.date)),
    [records],
  );
  const examAndVitals = useMemo(() => examAndVitalsVisits(records), [records]);

  return {
    medicalHistory,
    allergies,
    conditions,
    procedures,
    immunizations,
    examAndVitals,
    familyHistory,
    socialHistory: [],
    hasSocialSource: false,
    isLoading,
    error,
    refresh,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Health Concerns  ←  Condition: a provider's, or one the patient added
// (sourceVendor "patient", same fields, see createHealthConcern)
// ────────────────────────────────────────────────────────────────────────────

export interface HealthConcernRecord {
  id: string;
  concern: string;
  category: "symptom" | "condition" | "preventive" | "lifestyle" | "mental_health";
  severity: "mild" | "moderate" | "severe" | "unknown";
  /** As shown: the hospital's, or the patient's own mark (statusInfo says which). */
  status: ConditionStatus;
  statusInfo: ConditionStatusInfo;
  onsetDate: string; // "" when unknown
  lastUpdated: string; // "" when unknown
  notes: string; // "" when unknown
  icdCode: string;
  source: "provider" | "patient";
  /** The date it was entered (recorded_date); "" when unknown. */
  reportedDate: string;
  /** A provider's concern: the latest day the chart documented it (recorded /
   *  asserted date, from the record itself — never the upload day); "" when
   *  the chart gives none. The patient's own concern: "". */
  documentedDate: string;
}

export function adaptHealthConcern(record: ParsedRecord): HealthConcernRecord {
  const f = record.fields;
  // Inactive is its own status now (it used to be filed under Resolved, while
  // the "Inactive" tab counted recurrence/relapse — which means active again).
  const statusInfo = conditionStatusInfo(record);
  return {
    id: record.id,
    concern: firstStr(f.display_name) || "Health concern",
    category: "condition",
    severity: "unknown",
    status: statusInfo.status,
    statusInfo,
    // When it started, and only that: the day the chart documented it is
    // documentedDate (a provider's) or reportedDate (the patient's own), so
    // the two dates are never mixed up.
    onsetDate: dateOnly(f.onset_date),
    lastUpdated: conditionLastUpdated(record),
    notes: str(f.note),
    icdCode: str(f.icd10_code),
    source: record.sourceVendor === PATIENT_SOURCE_VENDOR ? "patient" : "provider",
    reportedDate: dateOnly(f.recorded_date),
    documentedDate: record.sourceVendor === PATIENT_SOURCE_VENDOR
      ? ""
      : [dateOnly(f.recorded_date), dateOnly(f.asserted_date)].filter(Boolean).sort().at(-1) ?? "",
  };
}

export interface HealthConcernsResult {
  /** From providers (every source except the patient). */
  concerns: HealthConcernRecord[];
  /** The ones the patient added themselves ("My Concerns"). */
  patientConcerns: HealthConcernRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useHealthConcerns(): HealthConcernsResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  const all = useMemo(() => {
    return records
      .filter((r) => r.resourceType === "Condition")
      .map(adaptHealthConcern)
      .sort((a, b) => b.onsetDate.localeCompare(a.onsetDate));
  }, [records]);
  const concerns = useMemo(() => all.filter((c) => c.source === "provider"), [all]);
  const patientConcerns = useMemo(
    () => all.filter((c) => c.source === "patient").sort((a, b) => b.reportedDate.localeCompare(a.reportedDate)),
    [all],
  );
  return {
    concerns,
    patientConcerns,
    isLoading,
    error,
    refresh,
  };
}
